/**
 * Reference data loading with a device-side cache.
 *
 * The server holds no cache — jobs and cost codes are read live from Sage
 * through cross-database views. The DEVICE caches them so a supervisor with no
 * signal can still start an entry, which is a client cache with a known
 * staleness rather than a server replica.
 */

import { useCallback, useEffect, useState } from 'react'
import { api } from '@/api/client'
import type {
  CrewMember,
  JobSummary,
  LaborCodeOption,
  WorkTypeOption,
} from '@/api/types'

const CACHE_KEY = 'nwl.cache.reference'
// Bumped when earnings codes were replaced by work types — a cache from the
// previous shape would feed the entry screen a list it cannot use.
const CACHE_VERSION = 2

interface ReferenceCache {
  version: number
  fetchedUtc: string
  jobs: JobSummary[]
  crew: CrewMember[]
  workTypes: WorkTypeOption[]
  laborCodes: LaborCodeOption[]
}

export interface Reference {
  jobs: JobSummary[]
  crew: CrewMember[]
  workTypes: WorkTypeOption[]
  laborCodes: LaborCodeOption[]
  loading: boolean
  error: string | null
  /** When the cache was last refreshed from the server; null if never. */
  fetchedUtc: string | null
  /** True when showing cached data because the network failed. */
  stale: boolean
  refresh: () => Promise<void>
}

function readCache(): ReferenceCache | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const c = JSON.parse(raw) as ReferenceCache
    return c.version === CACHE_VERSION ? c : null
  } catch {
    return null
  }
}

export function useReference(): Reference {
  const cached = readCache()

  const [jobs, setJobs] = useState<JobSummary[]>(cached?.jobs ?? [])
  const [crew, setCrew] = useState<CrewMember[]>(cached?.crew ?? [])
  const [workTypes, setWorkTypes] = useState<WorkTypeOption[]>(
    cached?.workTypes ?? [],
  )
  const [laborCodes, setLaborCodes] = useState<LaborCodeOption[]>(
    cached?.laborCodes ?? [],
  )
  const [fetchedUtc, setFetchedUtc] = useState<string | null>(
    cached?.fetchedUtc ?? null,
  )
  const [loading, setLoading] = useState(!cached)
  const [error, setError] = useState<string | null>(null)
  const [stale, setStale] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [j, c, w, l] = await Promise.all([
        api.jobs(),
        api.crew(),
        api.workTypes(true),
        api.laborCodes(),
      ])
      setJobs(j)
      setCrew(c)
      setWorkTypes(w)
      setLaborCodes(l)
      const now = new Date().toISOString()
      setFetchedUtc(now)
      setStale(false)
      try {
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            version: CACHE_VERSION,
            fetchedUtc: now,
            jobs: j,
            crew: c,
            workTypes: w,
            laborCodes: l,
          } satisfies ReferenceCache),
        )
      } catch {
        /* cache write is best-effort */
      }
    } catch (err) {
      // Falling back to cache is the whole point — surface staleness rather
      // than an empty screen, so a supervisor can still work.
      if (readCache()) {
        setStale(true)
        setError(null)
      } else {
        setError(err instanceof Error ? err.message : 'Could not load reference data')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return {
    jobs,
    crew,
    workTypes,
    laborCodes,
    loading,
    error,
    fetchedUtc,
    stale,
    refresh,
  }
}
